import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Share2, Send, Globe, Mail, Bot, Copy, Check, Loader2, FileText, ExternalLink, Eye, ChevronDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { PartnerLayout } from '@/components/PartnerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';

const METHOD_ICONS = { api: Globe, form_agent: Bot, email: Mail, copy_ready: Copy };
const METHOD_LABELS = { api: 'API Auto-Post', form_agent: 'AI Form Agent', email: 'Auto Email', copy_ready: 'Copy & Paste' };
const METHOD_COLORS = { api: 'bg-green-100 text-green-700', form_agent: 'bg-purple-100 text-purple-700', email: 'bg-blue-100 text-blue-700', copy_ready: 'bg-gray-100 text-gray-700' };
const STATUS_COLORS = { sent: 'bg-green-100 text-green-700', submitted: 'bg-green-100 text-green-700', filled: 'bg-amber-100 text-amber-700', ready: 'bg-blue-100 text-blue-700', pending_integration: 'bg-gray-100 text-gray-600', failed: 'bg-red-100 text-red-700', skipped: 'bg-gray-100 text-gray-500' };

export default function PartnerSyndication() {
  const { itemId } = useParams();
  const [item, setItem] = useState(null);
  const [targets, setTargets] = useState([]);
  const [records, setRecords] = useState([]);
  const [pressRelease, setPressRelease] = useState('');
  const [loading, setLoading] = useState(true);
  const [syndicating, setSyndicating] = useState(false);
  const [generatingPR, setGeneratingPR] = useState(false);
  const [copied, setCopied] = useState({});

  useEffect(() => {
    fetchData();
  }, [itemId]);

  const fetchData = async () => {
    try {
      const [itemRes, targetsRes, statusRes] = await Promise.all([
        api.get(`/vouchers/${itemId}`).catch(() => api.get(`/events/${itemId}`)),
        api.get('/syndication/targets'),
        api.get(`/syndication/status/${itemId}`),
      ]);
      setItem(itemRes.data);
      setTargets(targetsRes.data.targets || []);
      setRecords(statusRes.data.records || []);
    } catch {} finally { setLoading(false); }
  };

  const handleSyndicateAll = async () => {
    setSyndicating(true);
    toast.info('Syndicating to all platforms...');
    try {
      const itemType = item?.date ? 'event' : 'experience';
      const res = await api.post(`/syndication/syndicate/${itemId}?item_type=${itemType}`);
      toast.success(`Syndicated to ${res.data.targets_processed} platforms!`);
      fetchData();
    } catch { toast.error('Syndication failed'); }
    finally { setSyndicating(false); }
  };

  const handleGeneratePR = async () => {
    setGeneratingPR(true);
    try {
      const itemType = item?.date ? 'event' : 'experience';
      const res = await api.post(`/syndication/press-release/${itemId}?item_type=${itemType}`);
      setPressRelease(res.data.press_release);
      toast.success('Press release generated!');
    } catch { toast.error('Failed'); }
    finally { setGeneratingPR(false); }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [key]: true });
    setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 2000);
    toast.success('Copied!');
  };

  if (loading) return <PartnerLayout title="Syndication"><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div></PartnerLayout>;

  const syndicatedCount = records.filter(r => ['sent', 'submitted', 'filled'].includes(r.status)).length;
  const totalTargets = targets.length;

  return (
    <PartnerLayout title="Syndication" subtitle={item?.title || ''}>
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="text-center p-4"><p className="text-3xl font-bold text-indigo-600">{totalTargets}</p><p className="text-xs text-gray-500">Target Platforms</p></Card>
        <Card className="text-center p-4"><p className="text-3xl font-bold text-green-600">{syndicatedCount}</p><p className="text-xs text-gray-500">Syndicated</p></Card>
        <Card className="text-center p-4"><p className="text-3xl font-bold text-amber-600">{records.filter(r => r.status === 'ready').length}</p><p className="text-xs text-gray-500">Ready to Copy</p></Card>
        <Card className="text-center p-4"><p className="text-3xl font-bold text-purple-600">{records.filter(r => r.status === 'filled').length}</p><p className="text-xs text-gray-500">Forms Filled</p></Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSyndicateAll} disabled={syndicating}>
          {syndicating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Syndicating...</> : <><Share2 className="w-4 h-4 mr-2" /> Syndicate to All Platforms</>}
        </Button>
        <Button variant="outline" onClick={handleGeneratePR} disabled={generatingPR}>
          {generatingPR ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><FileText className="w-4 h-4 mr-2" /> Generate Press Release</>}
        </Button>
      </div>

      <Tabs defaultValue="targets">
        <TabsList>
          <TabsTrigger value="targets">Target Platforms ({totalTargets})</TabsTrigger>
          <TabsTrigger value="status">Syndication Status ({records.length})</TabsTrigger>
          <TabsTrigger value="press-release">Press Release</TabsTrigger>
        </TabsList>

        {/* Target Platforms */}
        <TabsContent value="targets" className="mt-4 space-y-3">
          {targets.map(target => {
            const Icon = METHOD_ICONS[target.method] || Globe;
            const record = records.find(r => r.target_id === target.id);
            return (
              <Card key={target.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Icon className="w-5 h-5 text-gray-600" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm">{target.name}</h3>
                        <Badge className={METHOD_COLORS[target.method] + ' text-[10px]'}>{METHOD_LABELS[target.method]}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{target.reach} · {target.category}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {record && <Badge className={STATUS_COLORS[record.status] + ' text-xs'}>{record.status}</Badge>}
                    {target.website && <a href={target.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600"><ExternalLink className="w-4 h-4" /></a>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        {/* Syndication Status */}
        <TabsContent value="status" className="mt-4 space-y-3">
          {records.length === 0 ? (
            <Card className="p-8 text-center text-muted-foreground">No syndication records yet. Click "Syndicate to All Platforms" to start.</Card>
          ) : records.map(record => (
            <Card key={record.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-sm">{record.target_name}</h3>
                    <Badge className={STATUS_COLORS[record.status] + ' text-xs'}>{record.status}</Badge>
                    <Badge variant="outline" className="text-[10px]">{record.method}</Badge>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(record.created_at).toLocaleDateString()}</span>
                </div>
                {record.content && (
                  <div className="mt-2 space-y-2">
                    {record.content.social_post && (
                      <div className="p-3 bg-gray-50 rounded text-sm">
                        <p className="text-xs text-muted-foreground mb-1">Social Post</p>
                        <p>{record.content.social_post}</p>
                        <Button variant="ghost" size="sm" className="mt-1 h-6 text-xs" onClick={() => copyToClipboard(record.content.social_post, `social-${record.id}`)}>
                          {copied[`social-${record.id}`] ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />} {copied[`social-${record.id}`] ? 'Copied' : 'Copy'}
                        </Button>
                      </div>
                    )}
                    {record.content.full_description && (
                      <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => copyToClipboard(record.content.full_description, `desc-${record.id}`)}>
                        {copied[`desc-${record.id}`] ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />} Copy Full Description
                      </Button>
                    )}
                    {record.screenshot && <a href={record.screenshot} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><Eye className="w-3 h-3" /> View Form Screenshot</a>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Press Release */}
        <TabsContent value="press-release" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Press Release</CardTitle>
              <CardDescription>AI-generated press release for media outreach</CardDescription>
            </CardHeader>
            <CardContent>
              {pressRelease ? (
                <div className="space-y-3">
                  <Textarea value={pressRelease} onChange={e => setPressRelease(e.target.value)} rows={15} className="font-mono text-sm" />
                  <Button variant="outline" onClick={() => copyToClipboard(pressRelease, 'pr')}>
                    {copied['pr'] ? <><Check className="w-4 h-4 mr-2" /> Copied</> : <><Copy className="w-4 h-4 mr-2" /> Copy Press Release</>}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">Generate a professional press release for this {item?.date ? 'event' : 'experience'}</p>
                  <Button onClick={handleGeneratePR} disabled={generatingPR}>
                    {generatingPR ? 'Generating...' : 'Generate Press Release'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </PartnerLayout>
  );
}
